/**
 * Value formatting.
 *
 * Two rules that are easy to get wrong and hard to spot afterwards:
 *
 *  - A cell under any `showAs` mode is a PERCENTAGE, whatever the measure's
 *    own format says. Formatting 0.294 as "0" because the measure is `hours`
 *    is the classic version of this bug.
 *  - `null` is not zero. An absent combination renders as an em dash, because
 *    "no rows matched" and "matched, summing to nothing" are different answers.
 */
import type {
  MeasureFormat,
  ReportModel,
  ReportSpec,
  ResolvedFormat,
  ShowAsMode,
  UIReportSpec,
} from '@clockwise/semantic-core';
import {
  decimalsFor,
  findMeasure,
  intlOptionsFor,
  readsNegative,
  resolveValueFormat,
  SCALES,
  showAsFor,
  toneFor,
} from '@clockwise/semantic-core';

/** What an absent value renders as. Never `0`. */
export const EMPTY_CELL = '—';

/**
 * The STYLE half of each format. The precision half is `FORMAT_DECIMALS` in
 * `@clockwise/semantic-core`, shared with the Excel writer so the screen and
 * the file it exports to cannot read differently.
 *
 * A format this table does not know falls back to `number` — see
 * {@link decimalsFor}, which makes the same choice for the digits. A browser
 * holding a cached bundle against a newer API is the ordinary way that happens.
 */
const STYLE: Record<MeasureFormat, Intl.NumberFormatOptions> = {
  number: {},
  percent: { style: 'percent' },
  currency: { style: 'currency', currency: 'USD' },
  hours: {},
};

/**
 * `Intl.NumberFormat` is expensive to construct and there are a handful of
 * (format, decimals) pairs in a whole application, so they are built once.
 *
 * Keyed on the RESOLVED precision rather than on the argument, which is what
 * keeps the key and the options in step. Keying on the raw argument meant
 * `null` and `undefined` shared an entry while building different formatters,
 * so one `null` poisoned the default entry for the life of the page.
 */
const formatters = new Map<string, Intl.NumberFormat>();

function formatter(format: MeasureFormat, decimals?: number | null): Intl.NumberFormat {
  const places = decimalsFor(format, decimals);
  const key = `${format}|${places}`;
  const cached = formatters.get(key);
  if (cached) return cached;
  const made = new Intl.NumberFormat('en-US', {
    ...(STYLE[format] ?? STYLE.number),
    // BOTH ENDS pinned. Padding is the point as much as truncating: a column
    // where 8 reads "8" and 0.55 reads "0.55" is one the eye cannot scan down.
    //
    // The hand-built Timesheet report's PREVIEW prints "8.00" — it renders the
    // `numeric(6, 2)` string straight from the driver. Its xlsx does not: that
    // writer sets no `numFmt` at all, so Excel shows the bare number in General
    // format. The two exports therefore still differ in shape, and only the
    // values are guaranteed equal; see docs/reporting/excel-export.md.
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  });
  formatters.set(key, made);
  return made;
}

/**
 * Render one value.
 *
 * `decimals` is the MEASURE's own declared precision — see `Measure.decimals`.
 * Passing it here rather than deciding by report kind is what keeps Amount
 * reading to the cent on a listing, in a pivot, in a chart tooltip and in the
 * xlsx, without any of those four knowing why.
 */
export function formatValue(
  value: number | null | undefined,
  format: MeasureFormat,
  decimals?: number | null,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_CELL;
  return formatter(format, decimals).format(value);
}

/**
 * Format a pivot cell, honouring `showAs`.
 *
 * The mode wins over the measure's own format — that is the whole point of
 * showing hours as a share.
 */
export function formatCell(
  value: number | null | undefined,
  format: MeasureFormat,
  mode: ShowAsMode,
  decimals?: number | null,
): string {
  // NO `decimals` on this branch, deliberately. A declared precision describes
  // the measure's OWN unit — hours to the hundredth, money to the cent — and
  // under a `showAs` the cell is not that quantity any more, it is a share.
  // Carrying "2" across would print a percentage to two decimals for no reason
  // anybody stated.
  if (mode !== 'raw') return formatValue(value, 'percent');
  return formatValue(value, format, decimals);
}

export function cellFormatter(
  spec: Pick<ReportSpec, 'showAs'>,
  measureId: string,
  format: MeasureFormat,
  decimals?: number | null,
) {
  const mode = showAsFor(spec, measureId);
  return (value: number | null | undefined) => formatCell(value, format, mode, decimals);
}

// ---------------------------------------------------------------------------
// The resolved path. Everything above takes a format and a precision; these
// take the whole answer `resolveValueFormat` gives, so the screen and the xlsx
// are built from ONE object rather than from two readings of a model.
// ---------------------------------------------------------------------------

/**
 * Render one value at a resolved format.
 *
 * Two rules that are easy to get wrong and hard to spot afterwards:
 *
 *  - The EM DASH is only ever an absent value. A zero styled as a dash prints a
 *    HYPHEN, because "no rows matched" and "matched, summing to nothing" are
 *    different answers and must not collide on one glyph.
 *  - Brackets are derived from what is PRINTED, not from `value < 0`. A value
 *    of -0.004 at two places prints "-0.00", and Excel brackets that too — so
 *    reading the sign off the rendered string is what keeps the two agreeing.
 *  - A SCALE divides what is printed and nothing else. The zero test above it
 *    reads the whole value, as Excel's zero section does, so 400 in millions is
 *    `$0.0M` and only a true zero takes the dash.
 *
 * The COLOUR is deliberately not here. This returns a string, and that string
 * is consumed by chart tooltips, axis ticks, the metric tile and the host's
 * chart theme, none of which can carry a colour. See {@link negativeClass}.
 */
export function formatResolved(value: number | null | undefined, f: ResolvedFormat): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_CELL;
  if (value === 0 && f.zero !== 'number') return f.zero === 'dash' ? '-' : '';

  const key = `r|${f.format}|${f.decimals}|${f.separators}|${f.symbol}`;
  let made = formatters.get(key);
  if (!made) {
    made = new Intl.NumberFormat('en-US', intlOptionsFor(f));
    formatters.set(key, made);
  }

  const scale = f.scale === 'none' ? undefined : SCALES[f.scale];
  const printed = made.format(scale ? value / scale.divisor : value);
  // The suffix belongs to the MAGNITUDE, so it goes on before any bracket:
  // `($1.2M)`, never `($1.2)M`.
  const text = scale ? `${printed}${scale.suffix}` : printed;
  if (text.charAt(0) !== '-') return text;
  // `redOnly` is Excel's own option: the colour carries the sign and nothing
  // else does. `minusRed` keeps it, which is the variant that survives a
  // greyscale print. Both ship; the report author picks.
  if (f.negative === 'redOnly') return text.slice(1);
  if (f.negative === 'parens' || f.negative === 'parensRed') return `(${text.slice(1)})`;
  return text;
}

/**
 * The compact form of a resolved value, for axis ticks where width is scarce.
 *
 * The axis and the tooltip above it label the same quantity and a reader
 * compares them, so this runs through {@link formatResolved} — the same
 * symbol, separators, sign and colour rules — and the magnitude suffix is the
 * ONLY difference between the two. Built the other way round (its own
 * formatter, as the pre-resolution version had) an author who removed the
 * currency symbol got a tooltip reading `48,210.00` under a tick still reading
 * `$48.2k`.
 *
 * A percentage is never compacted — it is already two or three characters —
 * and neither is anything below a thousand. A column the author already put in
 * K or M is left exactly as they chose: a tick reading `1.2M` over a tooltip
 * reading `1,234.6K` is two scales for one axis.
 */
export function formatCompactResolved(value: number | null | undefined, f: ResolvedFormat): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_CELL;
  const abs = Math.abs(value);
  if (f.format === 'percent' || f.scale !== 'none' || abs < 1_000) return formatResolved(value, f);

  const unit = abs >= 1_000_000 ? 'M' : 'k';
  const scaled = value / (unit === 'M' ? 1_000_000 : 1_000);
  // One decimal at this scale. `zero` is forced off because a scaled value can
  // round to zero without the underlying number being zero, and "-" on an axis
  // tick for 400 would be a lie.
  const text = formatResolved(scaled, { ...f, decimals: 1, zero: 'number' });
  // INSIDE a closing bracket. The bracket is part of the sign, not part of the
  // magnitude, so `($1.2M)` is right and `($1.2)M` is not.
  return text.endsWith(')') ? `${text.slice(0, -1)}${unit})` : `${text}${unit}`;
}

/** The class a negative cell takes, or `undefined`. Paired with `.rk-neg`. */
export function negativeClass(
  value: number | null | undefined,
  f: ResolvedFormat,
): string | undefined {
  return readsNegative(value, f) ? 'rk-neg' : undefined;
}

/**
 * The class a cell takes once highlight rules have had their say.
 *
 * A TONE REPLACES `rk-neg` RATHER THAN JOINING IT, and the precedence is
 * settled here in TypeScript instead of by the order two rules happen to sit in
 * the stylesheet. A tone ships a fill and the text colour that is readable on
 * it as a pair (see `TONE_ARGB` and the `--rk-tone-*` tokens); letting the
 * negative red survive on top would substitute a colour chosen for a white
 * background onto an amber one, which is exactly the unreadable combination the
 * named-tone palette exists to prevent.
 *
 * The number keeps its minus or its brackets either way — those live in the
 * formatted string, not in a class — so a tone never costs the reader the sign.
 */
export function cellClass(value: number | null | undefined, f: ResolvedFormat): string | undefined {
  const tone = toneFor(value, f);
  if (tone) return `rk-tone rk-tone-${tone}`;
  return negativeClass(value, f);
}

/**
 * Resolve one measure once, then render many cells with it.
 *
 * Per COLUMN rather than per cell: a column of hours is hours all the way
 * down, and resolving per cell would walk the model once per rendered number.
 */
export function resolvedFormatter(
  model: ReportModel,
  spec: Pick<UIReportSpec, 'showAs' | 'valueFormat'>,
  measureId: string,
) {
  const resolved = resolveValueFormat(findMeasure(model, measureId), spec, measureId);
  return {
    resolved,
    format: (value: number | null | undefined) => formatResolved(value, resolved),
    className: (value: number | null | undefined) => cellClass(value, resolved),
  };
}

/**
 * Compact form for axis labels and legends, where width is scarce.
 *
 * TAKES THE FORMAT, because an axis and the tooltip beside it label the same
 * quantity and a reader compares them. Without it every tick ran through the
 * `number` formatter: a percent measure carries a RATIO, so 0.294 drew a tick
 * reading "0" against a tooltip reading "29.4%", and a currency axis lost its
 * `$` entirely.
 *
 * A percentage is never compacted — it is already two or three characters —
 * and neither is any cell under a `showAs`, which is a percentage whatever the
 * measure is made of. Only the magnitudes that actually need the room get `k`
 * and `M`, and currency keeps its symbol when they do.
 */
export function formatCompact(
  value: number | null | undefined,
  format: MeasureFormat = 'number',
  mode: ShowAsMode = 'raw',
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_CELL;
  if (mode !== 'raw' || format === 'percent') return formatCell(value, format, mode);
  const abs = Math.abs(value);
  const unit = abs >= 1_000_000 ? 'M' : abs >= 1_000 ? 'k' : '';
  if (unit === '') return formatter(format).format(value);
  const scaled = (value / (unit === 'M' ? 1_000_000 : 1_000)).toFixed(1);
  return format === 'currency' ? `$${scaled}${unit}` : `${scaled}${unit}`;
}
